import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router';
import { CameraScan, hasDetector } from '../components/CameraScan.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, type Tone, TableWrap } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { encode } from '../lib/barcodes.ts';
import { dateTime, money, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

/**
 * Barcode: the scanner for store-room work and the labels it reads. A scanning session collects
 * what was scanned (goods received, a stock count, a move between locations, waste) and becomes
 * the stock movement when confirmed. Works with a handheld scanner (it types the code and Enter),
 * or with the phone's camera where the browser can read barcodes.
 */

type Kind = 'receive' | 'count' | 'transfer' | 'waste';
type Tab = 'scan' | 'labels' | 'history';

interface Line {
  id: string;
  item: string;
  name: string;
  unit: string;
  qty: number;
  unit_cost: number;
  scans: number;
  last_at: string;
  codes: string[];
  on_hand: number | null;
  difference: number | null;
  short: boolean;
  value: number;
}
interface SessionDetail {
  id: string;
  number: number;
  name: string;
  kind: Kind;
  state: 'open' | 'done' | 'cancelled';
  location: string;
  location_name: string;
  to_location: string | null;
  to_location_name: string | null;
  supplier: string | null;
  note: string | null;
  document: string | null;
  value: number;
  created_by_name: string | null;
  created_at: string;
  done_at: string | null;
  done_by_name: string | null;
  lines: Line[];
  totals: { items: number; units: number; scans: number; value: number; short: number };
  can: { scan: boolean; cost: boolean };
}
interface Overview {
  today: string;
  kpi: { open: number; done_today: number; scans_today: number; without_code: number; items: number; codes: number };
  open: { id: string; name: string; kind: Kind; created_at: string; location_name: string; created_by_name: string | null; lines: number; mine: boolean }[];
  locations: { id: string; name: string; code: string }[];
  can: { receive: boolean; count: boolean; transfer: boolean; waste: boolean; manage: boolean };
}
interface CodeRow {
  id: string;
  code: string;
  pack_qty: number;
  label: string | null;
  scans: number;
  last_scan_at: string | null;
}
interface Labels {
  items: { id: string; name: string; unit: string; active: boolean; supplier: string | null; codes: CodeRow[] }[];
  locations: { id: string; name: string; short: string; active: boolean; codes: { id: string; code: string; scans: number }[] }[];
  can: { manage: boolean };
}
interface Lookup {
  kind: 'item' | 'location' | 'product' | 'unknown';
  code: string;
  pack_qty?: number;
  label?: string | null;
  item?: { id: string; name: string; unit: string; unit_cost: number; on_hand: number; min_qty: number; active: boolean };
  stock?: { location: string; name: string; on_hand: number; min_qty: number }[];
  location?: { id: string; name: string; code: string };
  product?: { id: string; name: string; price: number };
}
interface HistoryRow {
  id: string;
  name: string;
  kind: Kind;
  state: 'open' | 'done' | 'cancelled';
  document: string | null;
  value: number;
  created_at: string;
  done_at: string | null;
  supplier: string | null;
  note: string | null;
  location_name: string;
  to_location_name: string | null;
  created_by_name: string | null;
  done_by_name: string | null;
  lines: number;
  scans: number;
}

const KINDS: Kind[] = ['receive', 'count', 'transfer', 'waste'];
const KIND_ICON: Record<Kind, IconName> = { receive: 'truck', count: 'list', transfer: 'transfer', waste: 'trash' };
const KIND_TONE: Record<Kind, Tone> = { receive: 'primary', count: 'info', transfer: 'neutral', waste: 'warn' };
const num = (n: number) => new Intl.NumberFormat(locale(), { maximumFractionDigits: 3 }).format(n);
const signed = (n: number) => `${n > 0 ? '+' : ''}${num(n)}`;
const toNumber = (s: string) => {
  const n = Number(s.replace(',', '.'));
  return s.trim() === '' || !Number.isFinite(n) ? null : n;
};

function KindBadge({ kind }: { kind: Kind }) {
  return <StatusBadge tone={KIND_TONE[kind]} icon={KIND_ICON[kind]} label={t(`bc.k_${kind}` as MessageKey)} />;
}

/** A barcode drawn as SVG bars, with its number underneath. */
export function BarcodeSvg({ code, height = 44 }: { code: string; height?: number }) {
  const bars = useMemo(() => encode(code), [code]);
  const quiet = 10;
  const width = bars.modules + quiet * 2;
  let x = quiet;
  const rects: React.ReactNode[] = [];
  bars.widths.forEach((w, i) => {
    if (i % 2 === 0) rects.push(<rect key={i} x={x} y={0} width={w} height={height} />);
    x += w;
  });
  return (
    <svg className="bc-svg" viewBox={`0 0 ${width} ${height + 12}`} role="img" aria-label={t('bc.barcode_of', { code })} preserveAspectRatio="xMidYMid meet">
      <rect x={0} y={0} width={width} height={height + 12} fill="white" />
      <g fill="black">{rects}</g>
      <text x={width / 2} y={height + 10} textAnchor="middle" fontSize={9} fontFamily="ui-monospace, monospace" fill="black">
        {code}
      </text>
    </svg>
  );
}

export function Barcode() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const { data: o, reload } = useAction<Overview>('barcode.overview');
  const tabs: { key: Tab; icon: IconName }[] = [
    { key: 'scan', icon: 'scan' },
    { key: 'labels', icon: 'qr' },
    { key: 'history', icon: 'clock' },
  ];
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'scan') as Tab;
  const open = params.get('s');
  const [version, setVersion] = useState(0);
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  return (
    <Shell>
      <div className="page reports bc-page">
        <div className="page-head">
          <div>
            <h1>{t('bc.title')}</h1>
            <p className="muted">{t('bc.subtitle')}</p>
          </div>
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : open ? null : (
          <section className="rep-kpis" aria-label={t('bc.at_a_glance')}>
            <Kpi label={t('bc.k_open')} value={String(o.kpi.open)} hint={t('bc.k_open_hint')} warn={o.kpi.open > 0} />
            <Kpi label={t('bc.k_done_today')} value={String(o.kpi.done_today)} hint={tn('bc.k_scans_today', o.kpi.scans_today)} />
            <Kpi label={t('bc.k_codes')} value={String(o.kpi.codes)} hint={tn('bc.k_items', o.kpi.items)} />
            <Kpi label={t('bc.k_without')} value={String(o.kpi.without_code)} hint={t('bc.k_without_hint')} warn={o.kpi.without_code > 0} />
          </section>
        )}
        {open ? null : (
          <div className="segmented tabs" role="tablist" aria-label={t('bc.sections')}>
            {tabs.map((x) => (
              <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'scan' ? null : x.key })}>
                <Icon name={x.icon} size={16} />
                {t(`bc.tab_${x.key}` as MessageKey)}
              </button>
            ))}
          </div>
        )}
        {!o ? null : open ? (
          <SessionView key={open} id={open} manage={o.can.manage} onBack={() => set({ s: null })} onChange={changed} />
        ) : tab === 'scan' ? (
          <ScanHome key={version} o={o} onOpen={(id) => set({ s: id })} onChange={changed} />
        ) : tab === 'labels' ? (
          <LabelsTab key={version} canManage={can('barcode.manage')} onChange={changed} />
        ) : (
          <HistoryTab key={version} onOpen={(id) => set({ s: id })} />
        )}
      </div>
    </Shell>
  );
}

// ---- the scan box ---------------------------------------------------------------------------------

/** Where codes come in: a handheld scanner types the code and Enter; people can type it too. */
function ScanBox({ onCode, busy, label, times, setTimes }: { onCode: (code: string) => Promise<void> | void; busy?: boolean; label: string; times?: string; setTimes?: (v: string) => void }) {
  const [text, setText] = useState('');
  const [camera, setCamera] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const send = async (code: string) => {
    const c = code.trim();
    if (!c) return;
    setText('');
    await onCode(c);
    input.current?.focus();
  };
  useEffect(() => {
    input.current?.focus();
  }, []);
  return (
    <div className="bc-scanbox">
      <div className="bc-scanrow">
        <label className="search bc-scan-input">
          <Icon name="scan" size={20} />
          <input
            ref={input}
            className="input"
            value={text}
            placeholder={label}
            aria-label={label}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            enterKeyHint="go"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void send(text);
              }
            }}
          />
        </label>
        {setTimes ? (
          <label className="bc-times">
            <span className="muted small">×</span>
            <input className="input" inputMode="decimal" value={times} aria-label={t('bc.times')} title={t('bc.times_hint')} onChange={(e) => setTimes(e.target.value)} />
          </label>
        ) : null}
        <Button variant="primary" icon="check" busy={busy} disabled={!text.trim()} onClick={() => void send(text)}>
          {t('bc.add')}
        </Button>
        {hasDetector() && !camera ? <IconButton icon="camera" variant="secondary" label={t('bc.camera_on')} onClick={() => setCamera(true)} /> : null}
      </div>
      {camera ? <CameraScan onCode={(c) => void send(c)} onClose={() => setCamera(false)} /> : null}
      <p className="muted small">{hasDetector() ? t('bc.scan_hint_camera') : t('bc.scan_hint')}</p>
    </div>
  );
}

// ---- home: check an item, start or resume a session -----------------------------------------------

function ScanHome({ o, onOpen, onChange }: { o: Overview; onOpen: (id: string) => void; onChange: () => Promise<void> }) {
  const [start, setStart] = useState<Kind | null>(null);
  const [found, setFound] = useState<Lookup | null>(null);
  const [assign, setAssign] = useState<string | null>(null);
  const { busy, run } = useSave();
  const look = (code: string) =>
    void run(async () => {
      setFound(await action<Lookup>('barcode.lookup', { code }));
    });
  const kinds = KINDS.filter((k) => o.can[k]);
  return (
    <div className="bc-home">
      <section className="card bc-check">
        <h2>
          <Icon name="search" size={18} /> {t('bc.check_title')}
        </h2>
        <ScanBox onCode={look} busy={busy} label={t('bc.check_placeholder')} />
        {found ? <LookupCard f={found} canManage={o.can.manage} onAssign={() => setAssign(found.code)} /> : null}
      </section>
      {o.open.length ? (
        <section>
          <h2 className="bc-h2">{t('bc.open_sessions')}</h2>
          <ul className="bc-open">
            {o.open.map((s) => (
              <li key={s.id}>
                <button type="button" className="bc-open-btn" onClick={() => onOpen(s.id)}>
                  <span className={`bc-kind-icon bc-kind-${s.kind}`}>
                    <Icon name={KIND_ICON[s.kind]} size={18} />
                  </span>
                  <span className="bc-open-text">
                    <strong>
                      {t(`bc.k_${s.kind}` as MessageKey)} · {s.location_name}
                    </strong>
                    <span className="muted small">
                      {s.name} · {tn('bc.n_items', s.lines)} · {s.mine ? t('bc.yours') : (s.created_by_name ?? '—')} · {dateTime(s.created_at)}
                    </span>
                  </span>
                  <span className="bc-open-go">
                    {t('bc.resume')} <Icon name="chevronRight" size={14} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section>
        <h2 className="bc-h2">{t('bc.start_title')}</h2>
        {kinds.length ? (
          <div className="bc-kinds">
            {kinds.map((k) => (
              <button key={k} type="button" className="bc-kind" onClick={() => setStart(k)}>
                <span className={`bc-kind-icon bc-kind-${k}`}>
                  <Icon name={KIND_ICON[k]} size={22} />
                </span>
                <strong>{t(`bc.start_${k}` as MessageKey)}</strong>
                <span className="muted small">{t(`bc.start_${k}_hint` as MessageKey)}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="muted">{t('bc.no_kinds')}</p>
        )}
      </section>
      {start ? (
        <StartDialog
          kind={start}
          locations={o.locations}
          onClose={() => setStart(null)}
          onStarted={async (id) => {
            setStart(null);
            await onChange();
            onOpen(id);
          }}
        />
      ) : null}
      {assign ? (
        <AssignDialog
          code={assign}
          onClose={() => setAssign(null)}
          onDone={async () => {
            setAssign(null);
            await onChange();
            setFound(await action<Lookup>('barcode.lookup', { code: assign }));
          }}
        />
      ) : null}
    </div>
  );
}

function LookupCard({ f, canManage, onAssign }: { f: Lookup; canManage: boolean; onAssign: () => void }) {
  if (f.kind === 'item' && f.item && f.stock) {
    return (
      <div className="bc-found ok" role="status">
        <div className="bc-found-head">
          <Icon name="box" size={18} />
          <strong>{f.item.name}</strong>
          {f.pack_qty && f.pack_qty !== 1 ? (
            <span className="mt-chip">
              {f.label ? `${f.label} · ` : ''}
              {t('bc.pack_of', { qty: num(f.pack_qty), unit: f.item.unit })}
            </span>
          ) : null}
          {!f.item.active ? <span className="mt-chip">{t('bc.not_in_use')}</span> : null}
        </div>
        <ul className="bc-stock">
          {f.stock.map((s) => (
            <li key={s.location} className={s.on_hand <= s.min_qty ? 'low' : undefined}>
              <span>{s.name}</span>
              <strong className="tabular">
                {num(s.on_hand)} {f.item!.unit}
              </strong>
              {s.on_hand <= s.min_qty && s.min_qty > 0 ? <span className="small">{t('bc.low')}</span> : null}
            </li>
          ))}
        </ul>
        <p className="muted small">{t('bc.cost_each', { cost: money(f.item.unit_cost), unit: f.item.unit })}</p>
      </div>
    );
  }
  if (f.kind === 'location' && f.location)
    return (
      <div className="bc-found ok" role="status">
        <div className="bc-found-head">
          <Icon name="pin" size={18} />
          <strong>{t('bc.is_location', { name: f.location.name })}</strong>
        </div>
      </div>
    );
  if (f.kind === 'product' && f.product)
    return (
      <div className="bc-found info" role="status">
        <div className="bc-found-head">
          <Icon name="bag" size={18} />
          <strong>{f.product.name}</strong>
          <span className="tabular">{money(f.product.price)}</span>
        </div>
        <p className="muted small">{t('bc.is_product')}</p>
      </div>
    );
  return (
    <div className="bc-found warn" role="status">
      <div className="bc-found-head">
        <Icon name="alert" size={18} />
        <strong>{t('bc.unknown', { code: f.code })}</strong>
      </div>
      {canManage ? (
        <Button size="sm" icon="plus" onClick={onAssign}>
          {t('bc.give_code')}
        </Button>
      ) : (
        <p className="muted small">{t('bc.unknown_ask')}</p>
      )}
    </div>
  );
}

function StartDialog({ kind, locations, onClose, onStarted }: { kind: Kind; locations: Overview['locations']; onClose: () => void; onStarted: (id: string) => Promise<void> }) {
  const [from, setFrom] = useState(locations[0]?.id ?? '');
  const [to, setTo] = useState(locations.find((l) => l.id !== locations[0]?.id)?.id ?? '');
  const [supplier, setSupplier] = useState('');
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const noteErr = tried && kind === 'waste' && note.trim().length < 3 ? t('bc.e_waste') : null;
  const toErr = tried && kind === 'transfer' && (!to || to === from) ? t('bc.e_to') : null;
  const save = () => {
    setTried(true);
    if ((kind === 'waste' && note.trim().length < 3) || (kind === 'transfer' && (!to || to === from))) return;
    void run(async () => {
      const s = await action<SessionDetail>('barcode.start', {
        kind,
        location: from || null,
        to_location: kind === 'transfer' ? to : null,
        supplier: kind === 'receive' ? supplier.trim() || null : null,
        note: note.trim() || null,
      });
      await onStarted(s.id);
    });
  };
  return (
    <Dialog
      title={t(`bc.start_${kind}` as MessageKey)}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant="primary" icon="scan" busy={busy} onClick={save}>
            {t('bc.start_go')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="muted">{t(`bc.start_${kind}_hint` as MessageKey)}</p>
        {locations.length > 1 ? (
          <Field label={kind === 'transfer' ? t('bc.from') : t('bc.where')}>
            <select className="input" value={from} onChange={(e) => setFrom(e.target.value)} data-autofocus>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {kind === 'transfer' ? (
          <Field label={t('bc.to')} error={toErr} hint={t('bc.to_hint')}>
            <select className="input" value={to} onChange={(e) => setTo(e.target.value)}>
              {locations
                .filter((l) => l.id !== from)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
            </select>
          </Field>
        ) : null}
        {kind === 'receive' ? (
          <Field label={t('bc.supplier')}>
            <input className="input" value={supplier} maxLength={120} placeholder={t('bc.supplier_placeholder')} onChange={(e) => setSupplier(e.target.value)} />
          </Field>
        ) : null}
        <Field label={kind === 'waste' ? t('bc.waste_reason') : t('bc.note')} required={kind === 'waste'} error={noteErr}>
          <input className="input" value={note} maxLength={200} placeholder={t(`bc.note_placeholder_${kind}` as MessageKey)} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- one session ---------------------------------------------------------------------------------

type Feedback = { tone: 'ok' | 'warn' | 'danger'; text: string; code?: string };

function SessionView({ id, manage, onBack, onChange }: { id: string; manage: boolean; onBack: () => void; onChange: () => Promise<void> }) {
  const { data: fetched } = useAction<SessionDetail>('barcode.get', { id });
  const [s, setS] = useState<SessionDetail | null>(null);
  useEffect(() => setS(fetched), [fetched]);
  const [times, setTimes] = useState('1');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [assign, setAssign] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [asking, setAsking] = useState<'confirm' | 'cancel' | null>(null);
  const { busy, run } = useSave();
  const [scanning, setScanning] = useState(false);
  const flash = useRef(0);

  const scan = async (code: string) => {
    setScanning(true);
    try {
      const r = await action<{ result: { kind: string; added?: number; name?: string; unit?: string }; session: SessionDetail }>('barcode.scan', { session: id, code, times: toNumber(times) ?? 1 });
      setS(r.session);
      if (r.result.kind === 'item') setFeedback({ tone: 'ok', text: t('bc.added', { qty: signed(r.result.added!), unit: r.result.unit!, name: r.result.name! }) });
      else if (r.result.kind === 'location') setFeedback({ tone: 'ok', text: t('bc.goes_to', { name: r.result.name! }) });
      else {
        setFeedback({ tone: 'warn', text: t(r.result.kind === 'product' ? 'bc.scan_product' : 'bc.unknown', { code }), code });
        navigator.vibrate?.([80, 60, 80]);
      }
      if (r.result.kind === 'item' || r.result.kind === 'location') navigator.vibrate?.(40);
      setTimes('1');
    } catch (err) {
      setFeedback({ tone: 'danger', text: err instanceof Error ? err.message : String(err) });
      navigator.vibrate?.([80, 60, 80]);
    } finally {
      flash.current += 1;
      setScanning(false);
    }
  };
  const setLine = (item: string, qty: number, unit_cost?: number | null) =>
    run(async () => {
      setS(await action<SessionDetail>('barcode.set_line', { session: id, item, qty, unit_cost: unit_cost ?? null }));
    });
  const remove = (item: string) =>
    void run(async () => {
      setS(await action<SessionDetail>('barcode.remove_line', { session: id, item }));
    });

  if (!s) return <Skeleton h={420} r="var(--radius-lg)" />;
  const open = s.state === 'open';
  return (
    <div className="bc-session">
      <div className="bc-session-head">
        <Button variant="ghost" icon="back" onClick={onBack}>
          {t('bc.back')}
        </Button>
        <div className="bc-session-title">
          <h2>
            {t(`bc.k_${s.kind}` as MessageKey)} · {s.location_name}
            {s.kind === 'transfer' ? (
              <>
                {' '}
                <Icon name="chevronRight" size={16} /> {s.to_location_name ?? '—'}
              </>
            ) : null}
          </h2>
          <span className="muted small">{[s.name, s.supplier, s.note, s.created_by_name, dateTime(s.created_at)].filter(Boolean).join(' · ')}</span>
        </div>
        <span className="bc-session-badges">
          <KindBadge kind={s.kind} />
          {s.state === 'done' ? <StatusBadge tone="ok" icon="check" label={t('bc.st_done')} /> : s.state === 'cancelled' ? <StatusBadge tone="neutral" icon="x" label={t('bc.st_cancelled')} /> : null}
        </span>
      </div>
      {open && s.can.scan ? (
        <section className="card bc-scan-card">
          <ScanBox onCode={scan} busy={scanning} label={t('bc.scan_placeholder')} times={times} setTimes={setTimes} />
          {feedback ? (
            <div key={flash.current} className={`bc-feedback ${feedback.tone}`} role="status" aria-live="polite">
              <Icon name={feedback.tone === 'ok' ? 'checkCircle' : 'alert'} size={18} />
              <span>{feedback.text}</span>
              {feedback.code && manage ? (
                <Button size="sm" icon="plus" onClick={() => setAssign(feedback.code!)}>
                  {t('bc.give_code')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}
      {open && !s.can.scan ? <p className="callout callout-info">{t('bc.someone_else')}</p> : null}
      {s.state === 'done' ? (
        <p className="callout callout-ok">
          <Icon name="checkCircle" size={16} />
          <span>
            {t(`bc.done_${s.kind}` as MessageKey, { doc: s.document ?? '', value: money(s.value) })} · {[s.done_by_name, s.done_at ? dateTime(s.done_at) : null].filter(Boolean).join(' · ')}
          </span>
        </p>
      ) : null}
      <section className="card bc-lines-card">
        <div className="mt-section-head">
          <h3 className="mt-h3">
            {tn('bc.n_items', s.totals.items)} · {tn('bc.n_scans', s.totals.scans)}
          </h3>
          {open && s.can.scan ? (
            <Button size="sm" icon="plus" onClick={() => setAdding(true)}>
              {t('bc.add_without')}
            </Button>
          ) : null}
        </div>
        {!s.lines.length ? (
          <EmptyState icon="scan" title={t('bc.nothing_yet')} body={t(`bc.nothing_yet_${s.kind}` as MessageKey)} />
        ) : (
          <ul className={`bc-lines bc-lines-${s.kind}`}>
            {s.lines.map((l) => (
              <LineRow key={l.id} l={l} kind={s.kind} editable={open && s.can.scan} onSet={setLine} onRemove={() => remove(l.item)} />
            ))}
          </ul>
        )}
        {s.lines.length ? (
          <div className="bc-totals">
            {s.kind === 'count' ? (
              <span>
                {t('bc.count_value')} <strong className={`tabular${s.totals.value < 0 ? ' text-danger' : ''}`}>{money(s.totals.value, { sign: true })}</strong>
              </span>
            ) : (
              <span>
                {t(`bc.value_${s.kind}` as MessageKey)} <strong className="tabular">{money(s.state === 'done' ? s.value : s.totals.value)}</strong>
              </span>
            )}
            {s.totals.short && open ? (
              <span className="text-danger">
                <Icon name="alert" size={14} /> {tn('bc.short', s.totals.short)}
              </span>
            ) : null}
          </div>
        ) : null}
      </section>
      {open && s.can.scan ? (
        <div className="bc-session-foot">
          <Button variant="ghost" icon="x" onClick={() => setAsking('cancel')}>
            {t('bc.cancel_session')}
          </Button>
          <Button variant="primary" icon="check" disabled={!s.lines.length || (s.kind === 'transfer' && !s.to_location)} onClick={() => setAsking('confirm')}>
            {t(`bc.confirm_${s.kind}` as MessageKey)}
          </Button>
        </div>
      ) : null}
      {adding ? (
        <AddItemDialog
          s={s}
          onClose={() => setAdding(false)}
          onAdd={async (item, qty) => {
            const ok = await setLine(item, qty);
            if (ok) setAdding(false);
          }}
        />
      ) : null}
      {assign ? (
        <AssignDialog
          code={assign}
          onClose={() => setAssign(null)}
          onDone={async () => {
            const code = assign;
            setAssign(null);
            await onChange();
            await scan(code);
          }}
        />
      ) : null}
      {asking ? (
        <Dialog
          title={asking === 'confirm' ? t(`bc.confirm_${s.kind}` as MessageKey) : t('bc.cancel_session')}
          onClose={() => setAsking(null)}
          size="sm"
          footer={
            <span className="foot-actions">
              <Button
                variant={asking === 'confirm' ? 'primary' : 'danger'}
                icon={asking === 'confirm' ? 'check' : 'x'}
                busy={busy}
                onClick={() =>
                  void run(
                    async () => {
                      setS(await action<SessionDetail>(asking === 'confirm' ? 'barcode.confirm' : 'barcode.cancel', { id }));
                      setAsking(null);
                      await onChange();
                    },
                    asking === 'confirm' ? t('bc.confirmed') : t('bc.cancelled'),
                  )
                }
              >
                {asking === 'confirm' ? t('bc.yes_confirm') : t('bc.yes_cancel')}
              </Button>
            </span>
          }
        >
          <p>{asking === 'confirm' ? tn(`bc.ask_confirm_${s.kind}`, s.totals.items, { from: s.location_name, to: s.to_location_name ?? '' }) : t('bc.ask_cancel')}</p>
          {asking === 'confirm' && s.totals.short ? <p className="callout callout-warn">{tn('bc.short_confirm', s.totals.short)}</p> : null}
        </Dialog>
      ) : null}
    </div>
  );
}

function LineRow({
  l,
  kind,
  editable,
  onSet,
  onRemove,
}: {
  l: Line;
  kind: Kind;
  editable: boolean;
  onSet: (item: string, qty: number, cost?: number | null) => Promise<boolean>;
  onRemove: () => void;
}) {
  const [qty, setQty] = useState(String(l.qty));
  const [cost, setCost] = useState((l.unit_cost / 100).toFixed(2));
  useEffect(() => setQty(String(l.qty)), [l.qty]);
  useEffect(() => setCost((l.unit_cost / 100).toFixed(2)), [l.unit_cost]);
  const qtyBad = toNumber(qty) === null || (toNumber(qty) ?? -1) < 0;
  const commit = () => {
    const q = toNumber(qty);
    const c = kind === 'receive' ? parseMoney(cost) : null;
    if (q === null || q < 0) return;
    if (q === l.qty && (kind !== 'receive' || c === l.unit_cost)) return;
    void onSet(l.item, q, kind === 'receive' ? c : null);
  };
  return (
    <li className={l.short ? 'short' : undefined}>
      <span className="bc-line-name">
        <strong>{l.name}</strong>
        <span className="muted small">
          {l.scans ? tn('bc.n_scans', l.scans) : t('bc.typed')}
          {kind !== 'receive' && l.on_hand !== null ? ` · ${t('bc.on_shelf', { qty: num(l.on_hand), unit: l.unit })}` : ''}
        </span>
      </span>
      <span className="bc-line-qty">
        {editable ? (
          <input
            className="input"
            inputMode="decimal"
            value={qty}
            aria-label={t('bc.qty_of', { name: l.name })}
            aria-invalid={qtyBad || undefined}
            onChange={(e) => setQty(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === 'Enter' && commit()}
          />
        ) : (
          <strong className="tabular">{num(l.qty)}</strong>
        )}
        <span className="muted small">{l.unit}</span>
      </span>
      {kind === 'count' ? (
        <span className={`bc-diff tabular${l.difference ? (l.difference < 0 ? ' minus' : ' plus') : ''}`}>
          {l.difference === null ? '' : l.difference === 0 ? t('bc.matches') : signed(l.difference)}
        </span>
      ) : kind === 'receive' ? (
        <span className="bc-line-cost">
          {editable ? (
            <input
              className="input"
              inputMode="decimal"
              value={cost}
              aria-label={t('bc.cost_of', { name: l.name, unit: l.unit })}
              onChange={(e) => setCost(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => e.key === 'Enter' && commit()}
            />
          ) : (
            <span className="tabular">{money(l.unit_cost)}</span>
          )}
          <span className="muted small">/{l.unit}</span>
        </span>
      ) : (
        <span className="bc-diff tabular">{l.short ? <span className="text-danger">{t('bc.not_enough')}</span> : money(l.value)}</span>
      )}
      {editable ? <IconButton icon="trash" label={t('bc.remove_named', { name: l.name })} onClick={onRemove} /> : <span />}
    </li>
  );
}

function AddItemDialog({ s, onClose, onAdd }: { s: SessionDetail; onClose: () => void; onAdd: (item: string, qty: number) => Promise<void> }) {
  const { data } = useAction<Labels>('barcode.codes');
  const [q, setQ] = useState('');
  const [pick, setPick] = useState<string | null>(null);
  const [qty, setQty] = useState('');
  const [tried, setTried] = useState(false);
  const items = (data?.items ?? []).filter((i) => i.active && (!q || i.name.toLowerCase().includes(q.toLowerCase()))).slice(0, 40);
  const chosen = data?.items.find((i) => i.id === pick);
  const n = toNumber(qty);
  const bad = n === null || n < 0 || (n === 0 && s.kind !== 'count');
  return (
    <Dialog
      title={t('bc.add_without')}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="plus"
            disabled={!pick}
            onClick={() => {
              setTried(true);
              if (!bad && pick) void onAdd(pick, n!);
            }}
          >
            {t('bc.add')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        {chosen ? (
          <>
            <div className="fs-picked">
              <strong>{chosen.name}</strong>
              <IconButton icon="x" label={t('bc.change_item')} onClick={() => setPick(null)} />
            </div>
            <Field label={t('bc.qty_in', { unit: chosen.unit })} required error={tried && bad ? t('bc.e_qty') : null}>
              <input className="input" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} data-autofocus />
            </Field>
          </>
        ) : (
          <>
            <label className="search">
              <Icon name="search" size={16} />
              <input className="input" type="search" value={q} placeholder={t('bc.search_items')} aria-label={t('bc.search_items')} onChange={(e) => setQ(e.target.value)} data-autofocus />
            </label>
            {!data ? (
              <Skeleton h={160} />
            ) : (
              <ul className="bc-pick">
                {items.map((i) => (
                  <li key={i.id}>
                    <button type="button" onClick={() => setPick(i.id)}>
                      <span>{i.name}</span>
                      <span className="muted small">{i.codes.length ? tn('bc.n_codes', i.codes.length) : t('bc.no_code')}</span>
                    </button>
                  </li>
                ))}
                {!items.length ? <li className="muted small">{t('bc.no_match')}</li> : null}
              </ul>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

/** Gives a code to an item: the one scanned, one typed, or a new one of the business's own. */
function AssignDialog({ code, item, onClose, onDone }: { code?: string | null; item?: { id: string; name: string; unit: string }; onClose: () => void; onDone: () => Promise<void> }) {
  const { data } = useAction<Labels>('barcode.codes');
  const [text, setText] = useState(code ?? '');
  const [pick, setPick] = useState(item?.id ?? '');
  const [pack, setPack] = useState('1');
  const [label, setLabel] = useState('');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const chosen = item ?? data?.items.find((i) => i.id === pick);
  const p = toNumber(pack);
  const packErr = tried && !(p !== null && p > 0) ? t('bc.e_pack') : null;
  const itemErr = tried && !pick ? t('bc.e_item') : null;
  const save = () => {
    setTried(true);
    if (!pick || !(p !== null && p > 0)) return;
    void run(async () => {
      await action('barcode.assign', { item: pick, code: text.trim() || null, pack_qty: p, label: label.trim() || null });
      await onDone();
    }, t('bc.code_saved'));
  };
  return (
    <Dialog
      title={item ? t('bc.add_code_to', { name: item.name }) : t('bc.give_code')}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('bc.save_code')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('bc.code')} hint={t('bc.code_hint')}>
          <input className="input bc-code-input" value={text} maxLength={64} autoComplete="off" spellCheck={false} onChange={(e) => setText(e.target.value)} data-autofocus={!code || undefined} />
        </Field>
        {!item ? (
          <Field label={t('bc.item')} required error={itemErr}>
            <select className="input" value={pick} onChange={(e) => setPick(e.target.value)} data-autofocus={code ? true : undefined}>
              <option value="">{t('bc.choose_item')}</option>
              {(data?.items ?? [])
                .filter((i) => i.active)
                .map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name} ({i.unit})
                  </option>
                ))}
            </select>
          </Field>
        ) : null}
        <div className="form-grid bc-pack-grid">
          <Field label={t('bc.pack')} required error={packErr} hint={chosen ? t('bc.pack_hint', { unit: chosen.unit }) : undefined}>
            <input className="input" inputMode="decimal" value={pack} onChange={(e) => setPack(e.target.value)} />
          </Field>
          <Field label={t('bc.label')} hint={t('bc.label_hint')}>
            <input className="input" value={label} maxLength={60} placeholder={t('bc.label_placeholder')} onChange={(e) => setLabel(e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

// ---- labels ---------------------------------------------------------------------------------------

interface PrintLabel {
  code: string;
  title: string;
  sub: string;
}

function LabelsTab({ canManage, onChange }: { canManage: boolean; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<Labels>('barcode.codes');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [copies, setCopies] = useState('1');
  const [printing, setPrinting] = useState(false);
  const [adding, setAdding] = useState<{ id: string; name: string; unit: string } | null>(null);
  const [editing, setEditing] = useState<{ code: CodeRow; unit: string; name: string } | null>(null);
  const { busy, run } = useSave();
  useEffect(() => {
    if (!printing) return;
    const done = () => setPrinting(false);
    window.addEventListener('afterprint', done);
    const id = setTimeout(() => window.print(), 80);
    return () => {
      clearTimeout(id);
      window.removeEventListener('afterprint', done);
    };
  }, [printing]);
  const changed = async () => {
    await reload();
    await onChange();
  };
  if (!data) return <Skeleton h={320} />;
  const items = data.items.filter((i) => !q || `${i.name} ${i.codes.map((c) => c.code).join(' ')}`.toLowerCase().includes(q.toLowerCase()));
  const missing = data.items.filter((i) => i.active && !i.codes.length).length;
  const all: PrintLabel[] = [
    ...data.items.flatMap((i) => i.codes.map((c) => ({ code: c.code, title: i.name, sub: c.pack_qty !== 1 ? c.label || t('bc.pack_of', { qty: num(c.pack_qty), unit: i.unit }) : i.unit }))),
    ...data.locations.flatMap((l) => l.codes.map((c) => ({ code: c.code, title: l.name, sub: t('bc.location_label') }))),
  ];
  const n = Math.max(1, Math.min(50, Math.round(toNumber(copies) ?? 1)));
  const toPrint = all.filter((x) => picked.has(x.code)).flatMap((x) => Array.from({ length: n }, () => x));
  const toggle = (code: string) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  const remove = (c: CodeRow) =>
    void run(async () => {
      await action('barcode.remove_code', { id: c.id });
      setPicked((p) => {
        const next = new Set(p);
        next.delete(c.code);
        return next;
      });
      await changed();
    }, t('bc.code_removed'));
  return (
    <>
      <div className="toolbar mt-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('bc.search_labels')} aria-label={t('bc.search_labels')} onChange={(e) => setQ(e.target.value)} />
        </label>
        {canManage && missing ? (
          <Button
            size="sm"
            icon="sparkle"
            busy={busy}
            onClick={() =>
              void run(
                async () => {
                  await action('barcode.fill_missing');
                  await changed();
                },
                tn('bc.made_codes', missing),
              )
            }
          >
            {tn('bc.make_missing', missing)}
          </Button>
        ) : null}
      </div>
      <div className="bc-print-bar" role="region" aria-label={t('bc.print_labels')}>
        <span>
          <strong>{tn('bc.n_picked', picked.size)}</strong>
          <button type="button" className="link small" onClick={() => setPicked(new Set(items.flatMap((i) => i.codes.map((c) => c.code))))}>
            {t('bc.pick_all')}
          </button>
          {picked.size ? (
            <button type="button" className="link small" onClick={() => setPicked(new Set())}>
              {t('bc.pick_none')}
            </button>
          ) : null}
        </span>
        <label className="bc-times">
          <span className="muted small">{t('bc.copies')}</span>
          <input className="input" inputMode="numeric" value={copies} aria-label={t('bc.copies')} onChange={(e) => setCopies(e.target.value)} />
        </label>
        <Button variant="primary" icon="printer" disabled={!picked.size} onClick={() => setPrinting(true)}>
          {tn('bc.print_n', toPrint.length)}
        </Button>
      </div>
      <section className="bc-label-list" aria-label={t('bc.items')}>
        {items.map((i) => (
          <article key={i.id} className={`card bc-label-item${i.active ? '' : ' inactive'}`}>
            <header>
              <strong>{i.name}</strong>
              <span className="muted small">
                {i.unit}
                {!i.active ? ` · ${t('bc.not_in_use')}` : ''}
              </span>
              {canManage && i.active ? (
                <Button size="sm" variant="ghost" icon="plus" onClick={() => setAdding({ id: i.id, name: i.name, unit: i.unit })}>
                  {t('bc.add_code')}
                </Button>
              ) : null}
            </header>
            {i.codes.length ? (
              <ul className="bc-codes">
                {i.codes.map((c) => (
                  <li key={c.id} className={picked.has(c.code) ? 'picked' : undefined}>
                    <label className="bc-pickbox">
                      <input type="checkbox" checked={picked.has(c.code)} onChange={() => toggle(c.code)} aria-label={t('bc.pick_label', { code: c.code })} />
                    </label>
                    <BarcodeSvg code={c.code} height={30} />
                    <span className="bc-code-meta">
                      <strong className="small">{c.pack_qty !== 1 ? c.label || t('bc.pack_of', { qty: num(c.pack_qty), unit: i.unit }) : t('bc.one_unit', { unit: i.unit })}</strong>
                      <span className="muted small">{c.scans ? tn('bc.scanned_times', c.scans) : t('bc.never_scanned')}</span>
                    </span>
                    {canManage ? (
                      <span className="bc-code-tools">
                        <IconButton icon="edit" label={t('bc.edit_code', { code: c.code })} onClick={() => setEditing({ code: c, unit: i.unit, name: i.name })} />
                        <IconButton icon="trash" label={t('bc.remove_code', { code: c.code })} onClick={() => remove(c)} />
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted small">{t('bc.no_code_yet')}</p>
            )}
          </article>
        ))}
        {!items.length ? <EmptyState icon="search" title={t('bc.no_match')} /> : null}
      </section>
      <h2 className="bc-h2">{t('bc.locations')}</h2>
      <TableWrap>
        <Table
          head={[t('bc.c_location'), t('bc.c_code'), t('bc.c_scans'), '']}
          phoneHide={[2]}
          empty={t('bc.no_locations')}
          rows={data.locations.map((l) => ({
            key: l.id,
            cells: [
              <strong key="n">{l.name}</strong>,
              l.codes.length ? (
                <span key="c" className="bc-loc-codes">
                  {l.codes.map((c) => (
                    <label key={c.id} className="bc-loc-code">
                      <input type="checkbox" checked={picked.has(c.code)} onChange={() => toggle(c.code)} aria-label={t('bc.pick_label', { code: c.code })} />
                      <code>{c.code}</code>
                    </label>
                  ))}
                </span>
              ) : (
                <span key="c" className="muted">
                  —
                </span>
              ),
              l.codes.reduce((sum, c) => sum + c.scans, 0) || '—',
              canManage && !l.codes.length && l.active ? (
                <Button
                  key="b"
                  size="sm"
                  icon="plus"
                  onClick={() =>
                    void run(async () => {
                      await action('barcode.assign', { location: l.id });
                      await changed();
                    }, t('bc.code_saved'))
                  }
                >
                  {t('bc.make_label')}
                </Button>
              ) : (
                ''
              ),
            ],
          }))}
        />
      </TableWrap>
      {adding ? (
        <AssignDialog
          item={adding}
          onClose={() => setAdding(null)}
          onDone={async () => {
            setAdding(null);
            await changed();
          }}
        />
      ) : null}
      {editing ? (
        <EditCodeDialog
          c={editing.code}
          unit={editing.unit}
          name={editing.name}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await changed();
          }}
        />
      ) : null}
      {printing
        ? createPortal(
            <div className="print-sheet print-a4">
              <div className="bc-print-grid">
                {toPrint.map((x, i) => (
                  <div key={i} className="bc-print-label">
                    <strong>{x.title}</strong>
                    <span>{x.sub}</span>
                    <BarcodeSvg code={x.code} />
                  </div>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function EditCodeDialog({ c, unit, name, onClose, onDone }: { c: CodeRow; unit: string; name: string; onClose: () => void; onDone: () => Promise<void> }) {
  const [pack, setPack] = useState(String(c.pack_qty));
  const [label, setLabel] = useState(c.label ?? '');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const p = toNumber(pack);
  const save = () => {
    setTried(true);
    if (!(p !== null && p > 0)) return;
    void run(async () => {
      await action('barcode.update_code', { id: c.id, pack_qty: p, label: label.trim() || null });
      await onDone();
    }, t('bc.code_saved'));
  };
  return (
    <Dialog
      title={t('bc.edit_code_title', { name })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('bc.save_code')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <BarcodeSvg code={c.code} />
        <div className="form-grid bc-pack-grid">
          <Field label={t('bc.pack')} required error={tried && !(p !== null && p > 0) ? t('bc.e_pack') : null} hint={t('bc.pack_hint', { unit })}>
            <input className="input" inputMode="decimal" value={pack} onChange={(e) => setPack(e.target.value)} data-autofocus />
          </Field>
          <Field label={t('bc.label')} hint={t('bc.label_hint')}>
            <input className="input" value={label} maxLength={60} placeholder={t('bc.label_placeholder')} onChange={(e) => setLabel(e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

// ---- history ------------------------------------------------------------------------------------

function HistoryTab({ onOpen }: { onOpen: (id: string) => void }) {
  const [kind, setKind] = useState<Kind | ''>('');
  const { data } = useAction<HistoryRow[]>('barcode.sessions', { kind: kind || null });
  return (
    <>
      <div className="toolbar mt-tools">
        <div className="segmented" role="radiogroup" aria-label={t('bc.show')}>
          {(['', ...KINDS] as const).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
              {k ? t(`bc.k_${k}` as MessageKey) : t('bc.all')}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <Skeleton h={300} />
      ) : (
        <TableWrap>
          <Table
            view="barcode.history"
            head={[t('bc.c_session'), t('bc.c_kind'), t('bc.c_where'), t('bc.c_items'), t('bc.c_value'), t('bc.c_by'), t('bc.c_when')]}
            phoneHide={[2, 3, 5]}
            empty={t('bc.no_history')}
            rows={data.map((r) => ({
              key: r.id,
              onClick: () => onOpen(r.id),
              cells: [
                <span key="n">
                  <strong>{r.name}</strong>
                  {r.document ? <span className="muted small"> · {r.document}</span> : null}
                </span>,
                <span key="k" className="bc-hist-kind">
                  <KindBadge kind={r.kind} />
                  {r.state !== 'done' ? <span className="muted small">{t(`bc.st_${r.state}` as MessageKey)}</span> : null}
                </span>,
                r.to_location_name ? `${r.location_name} → ${r.to_location_name}` : r.location_name,
                tn('bc.n_items', r.lines),
                r.state === 'done' ? money(r.value) : '—',
                r.done_by_name ?? r.created_by_name ?? '—',
                dateTime(r.done_at ?? r.created_at),
              ],
            }))}
          />
        </TableWrap>
      )}
    </>
  );
}
